import { useCallback, useEffect, useState } from "react";
import { useError } from "../contexts/ErrorContext";
import { errorToString } from "../types/typeGuards"; // Import the errorToString function
import { Message, Thread, parseMessage, parseThread } from "../types/types";
import log from "../utils/logger"; // Import the logger
import useFetchWithMsal from "./useFetchWithMsal";
import { threadsDeleteUrl, threadsRenameUrl } from "../utils/threadEndpoints";

function looksLike404(error: unknown): boolean {
  const msg =
    typeof error === "object" && error && "message" in error
      ? String((error as any).message)
      : String(error ?? "");
  return /status:\s*404\b/i.test(msg) || /\b404\b/.test(msg);
}

function fallbackSessionManagerEndpoint(endpoint: string): string | null {
  // Convertit .../<page>/session_manager/<x> -> .../session_manager/<x>
  // utile si le backend n'expose pas les routes session_manager par page.
  const m = endpoint.match(/^(.*)\/[^/]+\/session_manager\/(thread_ids|thread_history|feedback_update)\/?$/);
  if (!m) return null;
  return `${m[1]}/session_manager/${m[2]}`;
}

export const useThread = (
  threadsEndpoint: string,
  messagesEndpoint: string
) => {
  const renameThreadEndpoint = threadsRenameUrl(threadsEndpoint);
  const deleteThreadEndpoint = threadsDeleteUrl(threadsEndpoint);

  const [threads, setThreads] = useState<Thread[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const { execute } = useFetchWithMsal();
  const { addError } = useError();

  const fetchThreads = useCallback(async () => {
    log.debug("Fetching threads from endpoint:", threadsEndpoint); // Log the endpoint
    try {
      const response = await execute("POST", threadsEndpoint);
      if (response) {
        const threads = response.map(parseThread) as Thread[];
        threads.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
        setThreads(threads);
        log.debug("Threads fetched successfully:", threads); // Log success
        return threads;
      }
    } catch (error) {
      if (looksLike404(error)) {
        const fallback = fallbackSessionManagerEndpoint(threadsEndpoint);
        if (fallback) {
          try {
            log.warn(
              "[threads] endpoint 404, fallback to global session_manager",
              { threadsEndpoint, fallback }
            );
            const response = await execute("POST", fallback);
            if (response) {
              const threads = response.map(parseThread) as Thread[];
              threads.sort(
                (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
              );
              setThreads(threads);
              return threads;
            }
          } catch (e2) {
            log.error("Fallback fetchThreads failed:", e2);
          }
        }
      }
      log.error("Error fetching threads:", error); // Log error
      addError(`Error fetching threads: ${errorToString(error)}`);
    }
  }, [execute, threadsEndpoint, addError]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const fetchMessages = useCallback(
    async (threadId: string) => {
      log.debug("Fetching messages for thread ID:", threadId); // Log the thread ID
      try {
        const response = await execute("POST", messagesEndpoint, { threadId });
        if (response) {
          setMessages(response.messages.map(parseMessage));
          log.debug("Messages fetched successfully:", response.messages); // Log success
        }
      } catch (error) {
        if (looksLike404(error)) {
          const fallback = fallbackSessionManagerEndpoint(messagesEndpoint);
          if (fallback) {
            try {
              log.warn(
                "[messages] endpoint 404, fallback to global session_manager",
                { messagesEndpoint, fallback, threadId }
              );
              const response = await execute("POST", fallback, { threadId });
              if (response) {
                setMessages(response.messages.map(parseMessage));
                return;
              }
            } catch (e2) {
              log.error("Fallback fetchMessages failed:", e2);
            }
          }
        }
        log.error("Error fetching messages:", error); // Log error
        addError(`Error fetching messages: ${errorToString(error)}`);
      }
    },
    [execute, messagesEndpoint, addError]
  );

  const changeThread = useCallback(
    (thread: Thread | null, fetch: boolean = true) => {
      setSelectedThread(thread);
      if (fetch) {
        if (thread !== null) {
          log.debug("Selected thread changed, fetching messages."); // Log when a thread is selected
          fetchMessages(thread.id);
        } else {
          log.debug("No thread selected, clearing messages."); // Log when no thread is selected
          setMessages([]);
        }
      }
    },
    [setSelectedThread, setMessages, fetchMessages]
  );

  const deleteThread = useCallback(
    async (payload: { id: string; title: string }) => {
      log.debug("Deleting thread with ID:", payload.id);
      try {
        // Payload attendu par le backend :
        // { "id": "<thread.id>", "title": "<titre actuel>", "user_id": "<user>" }
        await execute("POST", deleteThreadEndpoint, {
          id: payload.id,
          title: payload.title,
        });

        log.warn(`[threads] delete OK id=${payload.id} endpoint=${deleteThreadEndpoint}`);
        setThreads((prev) => prev.filter((t) => t.id !== payload.id));

        if (selectedThread?.id === payload.id) {
          setSelectedThread(null);
          setMessages([]);
        }
      } catch (error) {
        log.warn(
          `[threads] delete FAIL id=${payload.id} endpoint=${deleteThreadEndpoint}`,
          error
        );
        log.error("Error deleting thread:", error);
        addError(`Error deleting thread: ${errorToString(error)}`);
      }
    },
    [execute, deleteThreadEndpoint, addError, selectedThread]
  );

  const renameThread = useCallback(
    async (payload: { id: string; oldTitle: string; newTitle: string }) => {
      log.debug("Renaming thread with ID:", payload.id, "to:", payload.newTitle);
      try {
        // Payload attendu par le backend :
        // { "id": "<thread.id>", "old_title": "<ancien titre>", "new_title": "<nouveau titre>", "user_id": "<user>" }
        const response = await execute("POST", renameThreadEndpoint, {
          id: payload.id,
          old_title: payload.oldTitle,
          new_title: payload.newTitle,
        });

        if (response) {
          const updatedThread = parseThread(response as any);
          setThreads((prev) =>
            prev.map((t) => (t.id === payload.id ? updatedThread : t))
          );
          if (selectedThread?.id === payload.id) {
            setSelectedThread(updatedThread);
          }
        } else {
          // Mise à jour optimiste locale si aucune réponse exploitable.
          setThreads((prev) =>
            prev.map((t) =>
              t.id === payload.id ? { ...t, title: payload.newTitle } : t
            )
          );
          if (selectedThread?.id === payload.id && selectedThread) {
            setSelectedThread({ ...selectedThread, title: payload.newTitle });
          }
        }
      } catch (error) {
        log.error("Error renaming thread:", error);
        addError(`Error renaming thread: ${errorToString(error)}`);
      }
    },
    [execute, renameThreadEndpoint, addError, selectedThread]
  );

  return {
    fetchThreads,
    threads,
    selectedThread,
    changeThread,
    fetchMessages,
    messages,
    setThreads,
    deleteThread,
    renameThread,
  };
};
